import React from 'react'

import { Box, Card, CardContent, Divider, Skeleton, Stack } from '@mui/material'

import { BlueprintHierarchyBar } from '../../features/market-analytics/components/BlueprintHierarchyBar/BlueprintHierarchyBar'
import { MarketAnalyticsFilterBarSkeleton } from '../../features/market-analytics/components/MarketAnalyticsFilterBar'

// Mirrors `GlossaryDescriptionCard`: a small section label over a few body lines.
const DescriptionCardSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width={160} sx={{ fontSize: '0.6875rem', mb: 1 }} />
      <Stack spacing={0.75}>
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="94%" />
        <Skeleton variant="text" width="62%" />
      </Stack>
    </CardContent>
  </Card>
)

// Mirrors `LevelStatsCard`: window-toggle header row + four dense stat tiles
// (2×2 on small screens, one row from `md` up).
const EventTypeStatsCardSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="text" width={160} sx={{ fontSize: '0.6875rem' }} />
        <Skeleton variant="rounded" width={168} height={28} sx={{ borderRadius: '32px' }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" sx={{ height: 100, borderRadius: '16px' }} />
        ))}
      </Box>
    </CardContent>
  </Card>
)

// Mirrors `EventTypeBlueprintsCard`: header copy, divider, then table rows.
const BlueprintsTableSkeleton: React.FC = () => (
  <Card>
    <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Skeleton variant="text" width="35%" sx={{ fontSize: '1.1rem', maxWidth: 320 }} />
      <Skeleton variant="text" width="50%" sx={{ fontSize: '0.875rem', mt: 0.5, maxWidth: 480 }} />
    </CardContent>
    <Divider />
    <Stack spacing={0} sx={{ px: 2, py: 1 }}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} variant="text" sx={{ fontSize: '0.75rem', height: 40 }} />
      ))}
    </Stack>
  </Card>
)

interface MarketEventTypeDetailPageSkeletonProps {
  // Taxonomy selections carried in the URL, forwarded so the filter-bar
  // placeholder reserves the same chip rows + footer the resolved bar renders.
  selectedCategoryCount?: number
  selectedGenreCount?: number
  selectedSubgenreCount?: number
}

// Full-page skeleton for `MarketEventTypeDetailPage`, rendered while the
// catalogue list (which supplies the type's name, parent category and child
// blueprints) is in flight — layout shape first, no centered spinner.
export const MarketEventTypeDetailPageSkeleton: React.FC<MarketEventTypeDetailPageSkeletonProps> = ({
  selectedCategoryCount = 0,
  selectedGenreCount = 0,
  selectedSubgenreCount = 0,
}) => {
  return (
    <Box aria-busy="true">
      <BlueprintHierarchyBar isLoading />

      {/* Filter bar: same card/row footprint and position (directly under the
          hierarchy bar) as the resolved page's MarketAnalyticsFilterBar. */}
      <Box sx={{ mt: 1.5 }}>
        <MarketAnalyticsFilterBarSkeleton isActive={selectedCategoryCount + selectedGenreCount + selectedSubgenreCount > 0} />
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Skeleton variant="text" width="40%" sx={{ fontSize: '1.375rem', maxWidth: 420 }} />
      </Box>

      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <DescriptionCardSkeleton />
        <EventTypeStatsCardSkeleton />
        <BlueprintsTableSkeleton />
      </Stack>
    </Box>
  )
}
